import { useEffect, useId, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A settings card that starts collapsed and opens from an arrow on the side.
 *
 * The customer detail tabs had grown to a dozen tall cards, most of which are
 * irrelevant to any given customer — EDI settings were as prominent on a
 * one-location taqueria as on The Melt. So the rule here is **relevance, not
 * uniformity**: a card whose feature is actually live opens itself (`defaultOpen`),
 * everything else stays a one-line header until asked for.
 *
 * `summary` is what earns that: collapsed must still answer "is this on, and set
 * to what", or closing the card would just hide the state instead of tidying it.
 * Pass a short chip — "On · aiorders@…", "Tue & Fri", "3 contacts" — never a
 * truncated paragraph.
 *
 * Open/closed is remembered per card (localStorage, keyed by `storageKey`), so a
 * card someone opens on purpose stays open on the next visit. A card that has
 * never been touched follows `defaultOpen`, which means turning a feature ON
 * surfaces its card without the user having to remember it exists.
 */
export function CollapsibleCard({
  title,
  icon,
  summary,
  desc,
  action,
  defaultOpen = false,
  storageKey,
  tone = 'default',
  children,
}: {
  title: string;
  icon?: ReactNode;
  /** Compact state shown while collapsed — keep it to a few words. */
  summary?: ReactNode;
  /** Longer explanation; only rendered once expanded. */
  desc?: ReactNode;
  /** Header-right control (a link, a badge). Rendered in both states. */
  action?: ReactNode;
  defaultOpen?: boolean;
  /** Stable key for remembering open/closed. Omit to make it session-only. */
  storageKey?: string;
  tone?: 'default' | 'active' | 'warn';
  children: ReactNode;
}) {
  const bodyId = useId();
  const [open, setOpen] = useState(() => {
    if (!storageKey) return defaultOpen;
    try {
      const saved = localStorage.getItem(`apbg_card_${storageKey}`);
      if (saved === 'open') return true;
      if (saved === 'closed') return false;
    } catch {
      // Private windows and blocked site data throw — fall through to the default.
    }
    return defaultOpen;
  });

  // A feature that gets switched on mid-session should reveal its card, but only
  // if the user hasn't already made a choice about this card themselves.
  useEffect(() => {
    if (!defaultOpen || !storageKey) return;
    try {
      if (localStorage.getItem(`apbg_card_${storageKey}`) === null) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, [defaultOpen, storageKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (storageKey) {
      try {
        localStorage.setItem(`apbg_card_${storageKey}`, next ? 'open' : 'closed');
      } catch {
        // Non-fatal: the card still opens, it just won't be remembered.
      }
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-brix-surface',
        tone === 'active'
          ? 'border-emerald-200'
          : tone === 'warn'
            ? 'border-amber-200'
            : 'border-brix-border',
      )}
    >
      {/* The toggle and the action are SIBLINGS: `action` is usually a link or a
          button, and nesting interactive elements inside a button is invalid
          markup that also breaks keyboard use. */}
      <div className="flex items-center gap-2 pr-4">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex-1 min-w-0 flex items-center gap-2.5 px-5 py-3 text-left hover:bg-brix-border/20 rounded-xl transition-colors"
        >
          <ChevronRight
            size={15}
            className={cn(
              'shrink-0 text-brix-muted transition-transform',
              open && 'rotate-90',
            )}
          />
          {icon}
          <span className="font-display text-sm font-semibold text-brix-ink shrink-0">
            {title}
          </span>
          {!open && summary && (
            <span className="text-[11px] text-brix-muted truncate min-w-0">{summary}</span>
          )}
        </button>
        {action && <span className="shrink-0 flex items-center gap-2">{action}</span>}
      </div>

      {open && (
        <div id={bodyId} className="px-5 pb-4 space-y-4">
          {desc && (
            <p className="text-xs text-brix-muted max-w-xl leading-relaxed">{desc}</p>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
